/**
 * The Plotting workspace. This one mode holds everything that used to be split
 * across Planning, Manual Plotting and Pilotage, kept as a calm short list rather
 * than a wall of tools:
 *
 *   Conditions — its own sheet (the wind/tide picture, overlay and weather).
 *   Routes     — into the route builder and the route manager.
 *   Chartwork  — the traditional chartwork palette (fix, running fix, DR, EP,
 *                VRM, EBL, set & drift, course to steer), manual or assisted.
 *   Pilotage   — transit lines, clearing lines and turns, in the pilotage plan.
 *
 * Building/planning happens here; following a built route is handed to Live, the
 * same hand-off as before. Weather and tide are scenario-driven in phase 1 and
 * fill from a real feed in phase 3 with no layout change.
 */
import { useState, useEffect } from 'react';
import { useShellStore } from '@/store/shell';
import { useChartPlotterStore, type ActiveTool } from '@/store';
import { useObjectsStore } from '@/store/objects';
import { useRoutingStore } from '@/store/routing';
import { usePassagePlanStore, PLAN_STEPS } from '@/store/passagePlan';
import { PlanStepPanel } from './PassagePlanSteps';
import { PLOT_TOOLS } from './modes';
import { ToolGlyph } from './icons';
import { Sheet } from './Sheet';

type Tab = 'routes' | 'plot' | 'pilotage';

export function PlanningSheet() {
  const [tab, setTab] = useState<Tab>('routes');
  const openSheet = useShellStore((s) => s.openSheet);
  const closeSheet = useShellStore((s) => s.closeSheet);
  const showToast = useShellStore((s) => s.showToast);
  const assisted = useShellStore((s) => s.plottingAssisted);
  const setAssisted = useShellStore((s) => s.setPlottingAssisted);

  const activeTool = useChartPlotterStore((s) => s.activeTool);
  const setActiveTool = useChartPlotterStore((s) => s.setActiveTool);

  const routeCount = useRoutingStore((s) => s.routes.length);

  // Assisted passage-plan conductor. It sequences the SAME tools the buckets use.
  const planStatus = usePassagePlanStore((s) => s.status);
  const planStepIndex = usePassagePlanStore((s) => s.stepIndex);
  const planStep = PLAN_STEPS[Math.min(planStepIndex, PLAN_STEPS.length - 1)];

  function startPlan() {
    usePassagePlanStore.getState().start();
    setTab('routes');
  }
  function resumePlan() {
    usePassagePlanStore.getState().resume();
    const id = PLAN_STEPS[usePassagePlanStore.getState().stepIndex].id;
    setTab(id === 'pilotage' ? 'pilotage' : 'routes');
  }
  // Going to a tool free-form while a plan is running pauses it, so it can resume.
  function selectTab(t: Tab) {
    if (usePassagePlanStore.getState().status === 'active') usePassagePlanStore.getState().pause();
    setTab(t);
  }
  // While the plan is active, the open bucket follows the current step.
  useEffect(() => {
    if (planStatus !== 'active') return;
    const id = PLAN_STEPS[Math.min(planStepIndex, PLAN_STEPS.length - 1)].id;
    setTab(id === 'pilotage' ? 'pilotage' : 'routes');
  }, [planStatus, planStepIndex]);

  // Arm a chartwork tool, or open its builder sheet. Same behaviour the old
  // Manual Plotting palette had, so a plot placed here is the same object.
  function pickTool(id: ActiveTool) {
    if (id === 'fix') return openSheet('fixbuilder');
    if (id === 'running_fix') return openSheet('runningfix');
    if (id === 'set_drift') return openSheet('derived');
    if (id === 'cts') return openSheet('cts');
    if (assisted && (id === 'dr' || id === 'ep')) return openSheet('tracks');
    setActiveTool(id);
    closeSheet();
  }

  return (
    <Sheet
      name="planning"
      title="Plotting"
      sub="Plan and plot in one place: the conditions, the routes, the chartwork, and pilotage. Build it here; follow a route over in Live."
    >
      {planStatus === 'idle' ? (
        <div className="acp-btn-row">
          <button type="button" className="acp-btn primary full" onClick={startPlan}>
            Plan a passage — step by step
          </button>
        </div>
      ) : (
        <div
          className="acp-conductor"
          style={{ border: '1px solid rgba(127,127,127,0.3)', borderRadius: 10, padding: 10, marginBottom: 10 }}
        >
          <div className="lt" style={{ marginBottom: 8 }}>
            <b>
              Passage plan · step {planStepIndex + 1} of {PLAN_STEPS.length}: {planStep.label}
            </b>
            <span>
              {planStatus === 'paused'
                ? 'Paused. Resume to carry on, or keep working free-form below.'
                : planStep.hint}
            </span>
          </div>
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn"
              onClick={() => usePassagePlanStore.getState().back()}
              disabled={planStepIndex === 0}
            >
              Back
            </button>
            {planStatus === 'paused' ? (
              <button type="button" className="acp-btn primary" onClick={resumePlan}>
                Resume
              </button>
            ) : (
              <button
                type="button"
                className="acp-btn primary"
                onClick={() => usePassagePlanStore.getState().next()}
              >
                {planStepIndex === PLAN_STEPS.length - 1 ? 'Finish' : 'Next'}
              </button>
            )}
            <button
              type="button"
              className="acp-btn"
              onClick={() => usePassagePlanStore.getState().exit()}
            >
              Exit
            </button>
          </div>
        </div>
      )}
      {planStatus !== 'idle' && <PlanStepPanel stepId={planStep.id} />}
      {planStatus !== 'idle' && planStep.id === 'conditions' && (
        <div style={{ border: '1px solid rgba(127,127,127,0.25)', borderRadius: 10, padding: 10, marginBottom: 10 }}>
          <p className="sub">
            Set the wind, tide and chart overlay for the passage, then come back and tap Next.
          </p>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary full" onClick={() => openSheet('conditions')}>
              Open conditions
            </button>
          </div>
        </div>
      )}
      <label className="acp-fieldlabel">Or open a tool</label>
      <div className="acp-seg">
        <button type="button" onClick={() => openSheet('conditions')}>
          Conditions
        </button>
        <button type="button" className={tab === 'routes' ? 'on' : ''} onClick={() => selectTab('routes')}>
          Route
        </button>
        <button type="button" className={tab === 'plot' ? 'on' : ''} onClick={() => selectTab('plot')}>
          Chartwork
        </button>
        <button type="button" className={tab === 'pilotage' ? 'on' : ''} onClick={() => selectTab('pilotage')}>
          Pilotage
        </button>
      </div>

      {tab === 'routes' && (
        <>
          <p className="sub">
            {routeCount === 0
              ? 'No routes yet. Open the route manager to start one, or long-press the chart to drop waypoints.'
              : `${routeCount} ${routeCount === 1 ? 'route' : 'routes'}. Open the manager to build, rename, show or hide, and pick one to follow.`}
          </p>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary full" onClick={() => openSheet('route')}>
              Open route manager
            </button>
          </div>
        </>
      )}

      {tab === 'plot' && (
        <>
          <div className="acp-seg">
            <button type="button" className={!assisted ? 'on' : ''} onClick={() => setAssisted(false)}>
              Fully manual
            </button>
            <button type="button" className={assisted ? 'on' : ''} onClick={() => setAssisted(true)}>
              Assisted
            </button>
          </div>
          <div className="acp-palette">
            {PLOT_TOOLS.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`acp-tool${activeTool === t.id ? ' on' : ''}`}
                onClick={() => pickTool(t.id)}
              >
                <div className="ti">
                  <ToolGlyph tool={t.id} />
                </div>
                <div className="tn">{t.name}</div>
                <div className="td">{t.detail}</div>
              </button>
            ))}
          </div>
          <div className="acp-btn-row">
            <button
              type="button"
              className="acp-btn full"
              onClick={() => {
                useObjectsStore.getState().clear();
                showToast('Plots cleared');
              }}
            >
              Clear all plots
            </button>
          </div>
        </>
      )}

      {tab === 'pilotage' && (
        <>
          <p className="sub">
            Pilotage marks for close-quarters work. Create a clearing bearing by tapping a charted
            object (a buoy, beacon, or light) and choosing “Set clearing bearing”; it guards the
            safe side and warns if we cross it.
          </p>
          <div className="acp-btn-row">
            <button type="button" className="acp-btn primary full" onClick={() => openSheet('pilotage')}>
              Open pilotage plan
            </button>
          </div>
          <p className="acp-note">
            Add transits and clearing or heading bearings inside the pilotage plan. Clearing bearings
            are live: drag the free end to set the bearing, flip the safe side, and they raise the
            cross alarm on the status strip.
          </p>
        </>
      )}
    </Sheet>
  );
}
